/**
 * (c) 2015 Ignite Sales, Inc.
 * User: aholmes
 * Date: 6/28/2016
 * Time: 9:52 AM
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import FormError from './FormError';
import { Input, Select } from 'react-form-controlled';

const defaultErrorMessage = {
  id: 'input.error',
  description: 'A default message to display when no error message is provided',
  defaultMessage: 'Input field has an error.',
};

// Required for strings/ids not defined by default in MessageDefinitions.jsx
const getDefaultStrings = (
  { labelMessage, errorMessage, placeholderMessage, options, field },
  formatMessage
) => {
  const loopOptions = () => {
    return Object.keys(options).reduce((agg, option) => {
      agg[option] = formatMessage(options[option]);
      return agg;
    }, {});
  };

  return {
    placeholder: placeholderMessage ||
      labelMessage || { id: `contactMe.${field}.placeholder` },
    error: errorMessage || { id: `contactMe.${field}.error` },
    default: labelMessage || { id: `contactMe.${field}` },
    ...(options && { options: loopOptions() }),
  };
};

function textInput(props) {
  const {
    type,
    name,
    hasError = false,
    errorMessage,
    labelMessage,
    placeholderMessage,
    formatMessage = (s) => s?.defaultMessage || s,
    disabled = false,
    ref,
    ...rest
  } = props;

  const msgObject = getDefaultStrings(
    { errorMessage, labelMessage, placeholderMessage, field: name },
    formatMessage
  );
  const errorNode =
    hasError &&
    ((formatMessage(msgObject.error) !== `contactMe.${name}.error` && (
      <FormError message={msgObject.error} />
    )) ||
      formatMessage(defaultErrorMessage));

  return (
    <div key={name} className={`form-group ${name}`}>
      <label htmlFor={name} className="form-label">
        {formatMessage(msgObject.default)}
      </label>
      <Input
        type={type}
        debounce={0}
        className="form-control"
        name={name}
        placeholder={formatMessage(msgObject.placeholder)}
        disabled={disabled}
        aria-label={formatMessage(msgObject.default)}
        ref={ref}
        {...rest}
      />
      {errorNode}
    </div>
  );
}

function checkboxInput(props) {
  const { name, labelMessage, ...rest } = props;

  return (
    <div key={name} className={`form-group checkbox ${name}`}>
      <label htmlFor={name} className="form-label">
        <FormattedMessage {...labelMessage} />{' '}
        <Input
          type="checkbox"
          name={name}
          id={name}
          aria-label={labelMessage.defaultMessage}
          {...rest}
        />
      </label>
    </div>
  );
}

function selectInput(props) {
  const {
    disabled = false,
    errorMessage = defaultErrorMessage,
    formatMessage,
    hasError = false,
    labelMessage,
    name,
    options = {},
    placeholderMessage,
    ...rest
  } = props;

  const msgObject = getDefaultStrings(
    { errorMessage, labelMessage, placeholderMessage, options, field: name },
    formatMessage
  );
  const errorNode =
    hasError &&
    ((formatMessage(msgObject.error) !== `contactMe.${name}.error` && (
      <FormError message={msgObject.error} />
    )) ||
      formatMessage(defaultErrorMessage));

  return (
    <div key={name} className={`form-group ${name}`}>
      <label htmlFor={name} className="form-label">
        {formatMessage(msgObject.default)}
      </label>
      <Select
        className="form-control"
        name={name}
        disabled={disabled}
        options={msgObject.options}
        placeholder={formatMessage(msgObject.placeholder)}
        aria-label={formatMessage(msgObject.default)}
        {...rest}
      />
      {errorNode}
    </div>
  );
}

export { textInput, checkboxInput, selectInput };
